import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { OnboardingProvider } from './context/OnboardingContext';

// Pages
import LandingPage from './pages/LandingPage';
import AuthPage from './pages/AuthPage';
import HomePage from './pages/HomePage';
import RecipeDetailPage from './pages/RecipeDetailPage';
import ChallengePage from './pages/ChallengePage';
import ChallengeDetailPage from './pages/ChallengeDetailPage';
import ProfilePage from './pages/ProfilePage';
import PublicProfilePage from './pages/PublicProfilePage';
import LeaderboardPage from './pages/LeaderboardPage';
import CreatorDashboardNew from './pages/CreatorDashboardNew';
import ChefHediPage from './pages/ChefHediPage';
import SettingsPage from './pages/SettingsPage';
import AdminDashboard from './pages/AdminDashboard';
import SubscriptionPage from './pages/SubscriptionPage';
import SupportPage from './pages/SupportPage';
import ValidationPage from './pages/ValidationPage';
import TipSuccessPage from './pages/TipSuccessPage';
import SubscriptionSuccessPage from './pages/SubscriptionSuccessPage';
import AuthCallbackPage from './pages/AuthCallbackPage';
import RegionPage from './pages/RegionPage';
import PrivateChefPage from './pages/PrivateChefPage';
import CulturalSeasonsPage from './pages/CulturalSeasonsPage';
import NotificationsPage from './pages/NotificationsPage';
import LegalPage from './pages/LegalPage';
import SearchPage from './pages/SearchPage';
import VideoFeedPage from './pages/VideoFeedPage';
import CreateVideoPage from './pages/CreateVideoPage';
import BARKAWalletPage from './pages/BARKAWalletPage';
import KYCWithdrawalPage from './pages/KYCWithdrawalPage';
import MessagesPage from './pages/MessagesPage';
import LiveListPage from './pages/LiveListPage';
import LiveStreamPage from './pages/LiveStreamPage';
import BroadcastPage from './pages/BroadcastPage';
import RecipesPage from './pages/RecipesPage';
import UnifiedFeedPage from './pages/UnifiedFeedPage';
import CreateContentPage from './pages/CreateContentPage';

// Layout - Using new AppLayout
import AppLayout from './components/layout/AppLayout';

import './App.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      retry: 1,
    },
  },
});

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F5F2]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#1E4ED8] to-[#3B82F6] flex items-center justify-center shadow-xl animate-pulse">
            <span className="text-3xl">🌶️</span>
          </div>
          <span className="text-[#1E4ED8] font-fraunces text-2xl font-bold">MHARHER</span>
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }
  
  return children;
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F5F2]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#1E4ED8] to-[#3B82F6] flex items-center justify-center shadow-xl animate-pulse">
            <span className="text-3xl">🌶️</span>
          </div>
          <span className="text-[#1E4ED8] font-fraunces text-2xl font-bold">MHARHER</span>
        </div>
      </div>
    );
  }
  
  // If authenticated, redirect to the main feed (new home)
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

// Route that shows landing for non-auth, redirects to app for auth
const LandingRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F5F2]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#1E4ED8] to-[#3B82F6] flex items-center justify-center shadow-xl animate-pulse">
            <span className="text-3xl">🌶️</span>
          </div>
          <span className="text-[#1E4ED8] font-fraunces text-2xl font-bold">MHARHER</span>
        </div>
      </div>
    );
  }
  
  // If authenticated, redirect to the main feed
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F5F2]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#1E4ED8] to-[#3B82F6] flex items-center justify-center shadow-xl animate-pulse">
            <span className="text-3xl">🌶️</span>
          </div>
          <span className="text-[#1E4ED8] font-fraunces text-2xl font-bold">MHARHER</span>
        </div>
      </div>
    );
  }
  
  const adminRoles = ['admin', 'super_admin', 'content_admin', 'community_manager', 'moderator', 'analyst'];
  if (!user || !adminRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/landing" element={<PublicRoute><LandingPage /></PublicRoute>} />
      <Route path="/auth" element={<PublicRoute><AuthPage /></PublicRoute>} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      
      {/* Public Profile - accessible without login */}
      <Route path="/u/:username" element={<PublicProfilePage />} />
      
      {/* Leaderboard - accessible without login */}
      <Route path="/leaderboard" element={<LeaderboardPage />} />
      
      {/* Legal Pages - Public */}
      <Route path="/legal/:type" element={<LegalPage />} />
      <Route path="/legal" element={<LegalPage />} />
      
      {/* Protected Routes with New AppLayout */}
      <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
        {/* Recipes (Main Page) - Social Discovery */}
        <Route path="/" element={<RecipesPage />} />
        
        {/* Unified Feed (TikTok style with videos, text posts, polls) */}
        <Route path="/feed" element={<UnifiedFeedPage />} />
        
        {/* Legacy routes redirect */}
        <Route path="/home" element={<Navigate to="/" replace />} />
        <Route path="/explore" element={<Navigate to="/" replace />} />
        
        {/* Core Features */}
        <Route path="/search" element={<SearchPage />} />
        <Route path="/challenges" element={<ChallengePage />} />
        <Route path="/challenge/:id" element={<ChallengeDetailPage />} />
        
        {/* Content */}
        <Route path="/recipe/:id" element={<RecipeDetailPage />} />
        <Route path="/video/:id" element={<VideoFeedPage />} />
        
        {/* Creation - Unified */}
        <Route path="/create" element={<CreateContentPage />} />
        <Route path="/create-video" element={<CreateContentPage />} />
        
        {/* User */}
        <Route path="/profile/:username" element={<ProfilePage />} />
        <Route path="/creator" element={<CreatorDashboardNew />} />
        <Route path="/wallet" element={<BARKAWalletPage />} />
        <Route path="/kyc" element={<KYCWithdrawalPage />} />
        <Route path="/withdraw" element={<KYCWithdrawalPage />} />
        <Route path="/notifications" element={<NotificationsPage />} />
        <Route path="/messages" element={<MessagesPage />} />
        <Route path="/live" element={<LiveListPage />} />
        <Route path="/live/:streamId" element={<LiveStreamPage />} />
        <Route path="/live/:streamId/broadcast" element={<BroadcastPage />} />
        
        {/* Discovery */}
        <Route path="/regions/:regionId" element={<RegionPage />} />
        <Route path="/private-chefs" element={<PrivateChefPage />} />
        <Route path="/seasons" element={<CulturalSeasonsPage />} />
        <Route path="/ranking" element={<LeaderboardPage />} />
        <Route path="/chef-hedi" element={<ChefHediPage />} />
        
        {/* Settings & Support */}
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/support" element={<SupportPage />} />
        <Route path="/subscriptions" element={<SubscriptionPage />} />
        <Route path="/validation" element={<ValidationPage />} />
        
        {/* Success Pages */}
        <Route path="/tip/success" element={<TipSuccessPage />} />
        <Route path="/subscription/success" element={<SubscriptionSuccessPage />} />
      </Route>
      
      {/* Admin Routes */}
      <Route path="/admin/*" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
      
      {/* Fallback - redirect to landing or main feed based on auth */}
      <Route path="*" element={<Navigate to="/landing" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <OnboardingProvider>
          <BrowserRouter>
            <ScrollToTop />
            <AppRoutes />
            <Toaster 
              position="top-center"
              toastOptions={{
                style: {
                  background: 'hsl(var(--card))',
                  color: 'hsl(var(--card-foreground))',
                  border: '1px solid hsl(var(--border))',
                },
              }}
            />
          </BrowserRouter>
        </OnboardingProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
